import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from "@mui/material";
import { SortableList } from "@platform/design-system";

import { Carrier } from "../types";

type TemplateOption = {
  name: string;
  url: string;
};

type GeneratePresentationDialogProps = {
  isOpen: boolean;
  closeModal: () => void;
  onGeneratePresentation: (selected: string, orderedCarriers: string[]) => void;
  isGeneratingPresentation: boolean;
  templates: TemplateOption[];
  carriers: Carrier[];
  onPreview: (templateName: string) => void;
};

const GeneratePresentationDialog: React.FC<GeneratePresentationDialogProps> = ({
  isOpen,
  closeModal,
  onGeneratePresentation,
  isGeneratingPresentation,
  templates,
  carriers,
  onPreview,
}) => {
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>("");
  const [orderedCarriers, setOrderedCarriers] = React.useState<string[]>([]);

  React.useLayoutEffect(() => {
    if (isOpen) {
      setOrderedCarriers(carriers.map((c) => c.name));
    }
  }, [isOpen, carriers]);

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTemplate(event.target.value);
  };

  const handleGenerate = () => {
    onGeneratePresentation(selectedTemplate, orderedCarriers);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={closeModal}>
      <DialogTitle>Generate Presentation</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This broker has multiple marketing presentations, please choose the
          option required for this RFP. Please note, this is the only
          opportunity to choose a template for this RFP project.
        </DialogContentText>

        <RadioGroup value={selectedTemplate} onChange={handleSelect}>
          {templates.map((template) => (
            <TemplateRow key={template.name}>
              <FormControlLabel
                value={template.name}
                control={<Radio />}
                label={<Typography>{template.name}</Typography>}
              />
              <PreviewButton
                variant="text"
                onClick={() => onPreview(template.url)}
              >
                Preview
              </PreviewButton>
            </TemplateRow>
          ))}
        </RadioGroup>
        <DialogContentText sx={{ mt: 3, mb: 2 }}>
          Sort the carriers for your presentation by clicking and dragging. This
          action will arrange the columns in the spreadsheet from left to right
          based on the sort level from top to bottom.
        </DialogContentText>
        <SortableList
          items={orderedCarriers}
          onOrderChange={setOrderedCarriers}
          renderContent={(item) => (
            <ListItemText primary={<Typography>{item}</Typography>} />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={!selectedTemplate || isGeneratingPresentation}
        >
          {isGeneratingPresentation ? "Generating..." : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TemplateRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const PreviewButton = styled(Button)(() => ({
  fontWeight: 500,
}));

export default GeneratePresentationDialog;
